import React, { useState } from "react";
import { Flame, Clapperboard, Layers, FastForward, Sun, Crop, Box, Eraser, Route, Shapes, Move, ImagePlay, Lightbulb, AudioLines, Blend, Focus, Check, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtINR, fmtUSD } from "@/lib/api";

// Animated burst/flame icon for BFX Action.
const ActionIcon = ({ className }) => (
  <span className={`bfx-action-ico relative inline-flex items-center justify-center ${className}`} data-testid="bfx-action-icon">
    <span className="bfx-action-ring absolute inset-0 rounded-full" />
    <Flame className="bfx-action-flame w-full h-full" />
  </span>
);

const ICONS = { "bfx-action": ActionIcon, "bfx-alphagen": Layers, "bfx-extend": FastForward, "bfx-hdr": Sun, "bfx-reframe": Crop, "bfx-cg-render": Box,
  "bfx-clean-plate": Eraser, "bfx-video-guide": Route, "bfx-ingredients": Shapes, "bfx-motion-track": Move, "bfx-cinemagraph": ImagePlay,
  "bfx-relight": Lightbulb, "bfx-foley": AudioLines, "bfx-morph-cut": Blend, "bfx-deblur": Focus };

const ACTION_PREVIEW = "https://customer-assets-39nsmqrw.emergentagent.net/wingman/8967fdc6-4e61-442d-9f17-609c6068a91f/attachments/94b1d42786d54bf2a9a32972c1cf20b3_Action.jpg";
export const iconBg = (id, base) => (id === "bfx-action" ? "bfx-action-bg text-white" : base);

export const BfxIcon = ({ id, className = "w-4 h-4" }) => {
  const I = ICONS[id] || Clapperboard;
  return <I className={className} />;
};

const short = (n) => n.replace(/^BFX\s*/, "");
const minUsd = (t) => Math.min(...[t.usd_per_sec, t.video_usd_per_sec, t.image_usd_per_sec].filter(Boolean));
const priceOf = (t, rate) => t.coming_soon ? "Coming Soon" : t.price_label ? t.price_label
  : `from ${fmtINR(minUsd(t) * (rate || 0))} · ${fmtUSD(minUsd(t))}/s`;

const Option = ({ t, active, rate, onPick, onHover }) => (
  <button type="button" onClick={onPick} onMouseEnter={onHover} data-testid={`bfx-tool-${t.id}`}
    className={`w-full text-left flex items-start gap-2.5 p-2 rounded-lg transition-colors hover:bg-[#0747a1]/25 ${active ? "bg-[#0747a1]/30" : ""} ${t.coming_soon ? "opacity-70" : ""}`}>
    <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg(t.id, active ? "bfx-gradient-bg text-white" : "bg-[#0747a1]/30 text-[#64b7f6]")}`}><BfxIcon id={t.id} /></span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-medium text-white"><span className="text-[#41a7f5]">BFX</span> {short(t.name)}
        {active && <Check className="w-3.5 h-3.5 text-[#64b7f6]" />}</span>
      <span className="block text-xs text-zinc-500 line-clamp-1">{t.tagline}</span>
    </span>
    <span className="text-[10px] font-mono text-[#64b7f6]/80 whitespace-nowrap mt-0.5">{priceOf(t, rate)}</span>
  </button>
);

const Preview = ({ t, rate }) => t ? (
  <div className="flex-1 p-5 flex flex-col" data-testid="bfx-picker-preview">
    {t.id === "bfx-action"
      ? <img src={ACTION_PREVIEW} alt="BFX Action preview" data-testid="bfx-action-preview" className="h-32 w-full rounded-xl object-cover shadow-[0_10px_30px_rgba(249,115,22,0.35)]" />
      : <div className="h-32 rounded-xl bfx-gradient-bg flex items-center justify-center shadow-[0_10px_30px_rgba(20,119,210,0.35)]"><BfxIcon id={t.id} className="w-12 h-12 text-white" /></div>}
    <div className="mt-4 font-heading text-lg font-bold text-white"><span className="bfx-gradient-text">BFX</span> {short(t.name)}</div>
    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{t.tagline}</p>
    <div className="mt-3 text-[11px] font-mono text-[#64b7f6]">{priceOf(t, rate)}</div>
    {t.coming_soon && <div className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-400"><Clock className="w-3.5 h-3.5 text-[#41a7f5]" />Coming Soon — unlocks automatically</div>}
  </div>
) : <div className="flex-1" />;

export default function BfxToolPicker({ tools, value, onChange, rate }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const active = tools.find((t) => t.id === value);
  const groups = [{ label: "Available", items: tools.filter((t) => !t.coming_soon) }, { label: "Coming Soon", items: tools.filter((t) => t.coming_soon) }];
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); setHover(null); }}>
      <PopoverTrigger asChild>
        <button data-testid="bfx-tool-picker-trigger" className="ax-chip text-white font-medium !border-[#1477d2]/60">Change</button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" sideOffset={44}
        className="w-[620px] p-0 bg-[#070B14] border-[#1065c0]/50 text-zinc-100 rounded-xl shadow-2xl flex" data-testid="bfx-tool-picker-popover">
        <div className="w-[360px] shrink-0 border-r border-[#1065c0]/40 max-h-[480px] overflow-y-auto p-1.5" data-testid="bfx-tools">
          {groups.map((g) => (
            <div key={g.label} className="mb-1.5">
              <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#64b7f6]/70">{g.label}</div>
              {g.items.map((t) => <Option key={t.id} t={t} rate={rate} active={t.id === value}
                onHover={() => setHover(t)} onPick={() => { onChange(t.id); setOpen(false); }} />)}
            </div>
          ))}
        </div>
        <Preview t={hover || active} rate={rate} />
      </PopoverContent>
    </Popover>
  );
}
